import { Button, Dialog, DialogBody, DialogSurface, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowDownload20Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postMessageToParent } from '../../../libs/messaging';
import { onPanelHidden } from '../../../libs/panelVisibility';

const useClasses = makeStyles({
    inline: {
        position: 'relative',
        display: 'inline-block',
    },
    download: {
        position: 'absolute',
        top: tokens.spacingVerticalXS,
        right: tokens.spacingHorizontalXS,
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: tokens.shadow4,
    },
    thumbnailButton: {
        display: 'block',
        padding: 0,
        border: 'none',
        background: 'none',
        cursor: 'zoom-in',
    },
    surface: {
        maxWidth: '95vw',
        // The surface is position:fixed with inset:0, so width:auto would stretch it to the viewport.
        width: 'fit-content',
        padding: tokens.spacingHorizontalS,
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: tokens.spacingVerticalXS,
    },
    full: {
        maxWidth: '100%',
        maxHeight: '85vh',
        objectFit: 'contain',
    },
});

interface ChatImagePreviewProps {
    src: string;
    alt: string;
    thumbnailClassName: string;
}

const downloadFileName = (src: string): string => `image.${/^data:image\/(\w+)/.exec(src)?.[1] ?? 'png'}`;

/** Chat-bubble thumbnail that opens the image at full size, so UI detail in a screenshot stays legible. */
export const ChatImagePreview: FC<ChatImagePreviewProps> = ({ src, alt, thumbnailClassName }) => {
    const { t } = useTranslation();
    const classes = useClasses();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        postMessageToParent({ event: 'imagePreview', data: { open: true } });
        const stopListening = onPanelHidden(() => {
            setOpen(false);
        });
        return () => {
            stopListening();
            postMessageToParent({ event: 'imagePreview', data: { open: false } });
        };
    }, [open]);

    return (
        <>
            <div className={classes.inline}>
                <button
                    type="button"
                    className={classes.thumbnailButton}
                    title={t('chatHistory.imagePreviewOpen')}
                    onClick={() => {
                        setOpen(true);
                    }}
                >
                    <img src={src} alt={alt} className={thumbnailClassName} />
                </button>
                <Button
                    as="a"
                    size="small"
                    className={classes.download}
                    icon={<ArrowDownload20Regular />}
                    href={src}
                    download={downloadFileName(src)}
                    aria-label={t('chatHistory.imagePreviewDownload')}
                    title={t('chatHistory.imagePreviewDownload')}
                />
            </div>
            <Dialog
                open={open}
                onOpenChange={(_, data) => {
                    setOpen(data.open);
                }}
            >
                <DialogSurface className={classes.surface} aria-label={alt}>
                    <DialogBody className={classes.body}>
                        <Button
                            appearance="subtle"
                            icon={<Dismiss24Regular />}
                            aria-label={t('chatHistory.imagePreviewClose')}
                            onClick={() => {
                                setOpen(false);
                            }}
                        />
                        <img src={src} alt={alt} className={classes.full} />
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};
